import { observable, action, computed } from 'mobx';
import NumberValidator from 'utils/numberValidator';
import { getIndexForItem } from './helpers';

export class CartItems {
	@observable cartUpdates = [];
	@observable itemCancelled = false;
	@observable toggleWarning = false;

	@computed
	get isCurrentCartEmpty() {
		return !this.cartUpdates.length;
	}

	@computed
	get cartHasBeenUpdated() {
		return this.cartUpdates.length > 0;
	}

	@computed
	get hasError() {
		return this.cartUpdates.filter(o => !!o.errorMsgId).length > 0;
	}

	@action
	addToCurrentCart(item) {
		const index = getIndexForItem(this.cartUpdates, item, item.is_pack);

		item.initial_order_type = item.order_type;

		if (index >= 0) {
			this.cartUpdates[index] = item;
			this.cartUpdates.push(item); // trigger hasError
			const lastItemindex = -1;
			this.cartUpdates.splice(lastItemindex, 1);
		} else {
			this.cartUpdates.push(item);
		}

		return index;
	}

	@action
	addUpdate(item, value, prop) {
		const index = getIndexForItem(this.cartUpdates, item, item.is_pack);
		const errorMsgID = item.errorMsgId;

		if (index >= 0) {
			this.cartUpdates[index].updates[prop] = this.cartUpdates[index].updates[
				prop
			]
				? this.cartUpdates[index].updates[prop] + 1
				: 1;

			if (value && prop) {
				const item = this.cartUpdates[index];
				item[prop] = value;
				item.errorMsgId = errorMsgID;
			}
			this.cartUpdates.push(item); // trigger hasError
			const lastItemindex = -1;
			this.cartUpdates.splice(lastItemindex, 1);
		} else {
			item.updates = {};
			item.updates[prop] = value;
			this.cartUpdates.push(item);
		}
	}

	@action
	deleteUpdate(item, prop) {
		const index = getIndexForItem(this.cartUpdates, item, item.is_pack);

		if (index >= 0) {
			if (
				this.cartUpdates[index].updates.hasOwnProperty(prop) ||
				this.cartUpdates[index].updates[prop] === 0
			) {
				delete this.cartUpdates[index].updates[prop];
			}

			if (Object.keys(this.cartUpdates[index].updates).length === 0) {
				this.cartUpdates.splice(index, 1);
			}
		}
	}

	@action
	addConfiguration(config, cartItem) {
		const cartData = this.cartUpdates;

		const index = getIndexForItem(cartData, cartItem, cartItem.is_pack);
		let configIndex = -1;

		if (cartData[index].configurations) {
			configIndex = cartData[index].configurations.findIndex(
				item => item.id === config.id && item.number === config.number
			);
		} else {
			cartData[index].configurations = [];
		}

		if (configIndex >= 0) {
			cartData[index].configurations[configIndex] = config;
		} else {
			cartData[index].configurations.push(config);
		}
	}

	@action
	deleteConfiguration(config, cartItem) {
		const cartData = this.cartUpdates;

		const index = getIndexForItem(cartData, cartItem, cartItem.is_pack);
		let configIndex = -1;

		if (cartData[index].configurations) {
			configIndex = cartData[index].configurations.findIndex(
				item => item.id === config.id && item.number === config.number
			);
		}

		if (configIndex >= 0) {
			cartData[index].configurations.splice(configIndex, 1);
		}
	}

	@action
	deleteFromCart(item) {
		const index = getIndexForItem(this.cartUpdates, item, item.is_pack);
		if (index >= 0) {
			this.cartUpdates[index].quantity_ordered = null;
			this.cartUpdates[index].drop_ship = null;
			this.cartUpdates[index].configurations = null;
			this.cartUpdates[index].errorMsgId = null;

			this.cartUpdates.splice(index, 1);
		}
	}

	@action
	emptyCurrentCart() {
		this.cartUpdates.map(item => {
			item.quantity_ordered = null;
			item.errorMsgId = null;
			item.configurations = [];
		});

		this.cartUpdates = [];
	}

	@action
	toggleWarningMessage() {
		this.toggleWarning = !this.toggleWarning;
	}

	updateQuantity(index, quantity) {
		this.cartUpdates[index].quantity_ordered = quantity;
	}

	itemHasBeenUpdated(updates) {
		return Object.keys(updates).reduce(
			(sum, key) => sum + (typeof updates[key] === 'number' ? updates[key] : 0),
			0
		);
	}

	getTotalQuantity(items, id) {
		return items
			? items.reduce(
					(sum, item) => (item.id === id ? sum + item.quantity_ordered : sum),
					0
				)
			: 0;
	}

	isSoldOut(item) {
		return item.quantity === 'Sold Out' || item.quantity === 0;
	}

	@action
	getErrorMessageId(isFromCart, availableQuantity, totalQuantityOrdered, item) {
		let errorMessageToReturn = null;

		if (
			isFromCart &&
			(item.quantity_ordered === null ||
				typeof item.quantity_ordered === 'undefined')
		) {
			errorMessageToReturn = 'addtocart.EmptyField';
		}

		if (
			!isFromCart &&
			availableQuantity === 0 &&
			item.quantity_ordered &&
			item.quantity_ordered > 0
		) {
			errorMessageToReturn = 'addtocart.MaximumQuantity';
		}

		const numberValidator = new NumberValidator();

		if (
			(isFromCart &&
				numberValidator.isGreaterThan(
					totalQuantityOrdered,
					availableQuantity
				)) ||
			(!isFromCart &&
				numberValidator.isGreaterThan(item.quantity_ordered, availableQuantity))
		) {
			errorMessageToReturn =
				isFromCart || availableQuantity === item.quantity
					? 'addtocart.GreaterQuantity'
					: 'addtocart.CartGreaterQuantity';
		}

		item.errorMsgId = errorMessageToReturn;
		return errorMessageToReturn;
	}

	getNumberOfDropshipChanges(item) {
		return item.is_pack
			? this.cartUpdates.filter(
					cartItem =>
						cartItem.pack_id === item.pack_id &&
						((cartItem.drop_ship_address !== null &&
							cartItem.initial_drop_ship_address === null) ||
							(cartItem.drop_ship_address === null &&
								cartItem.initial_drop_ship_address !== null) ||
							(cartItem.drop_ship_address !== null &&
								cartItem.initial_drop_ship_address !== null &&
								cartItem.drop_ship_address.name !==
									cartItem.initial_drop_ship_address.name))
				).length
			: this.cartUpdates.filter(
					cartItem =>
						cartItem.id === item.id &&
						((cartItem.drop_ship_address !== null &&
							cartItem.initial_drop_ship_address === null) ||
							(cartItem.drop_ship_address === null &&
								cartItem.initial_drop_ship_address !== null) ||
							(cartItem.drop_ship_address !== null &&
								cartItem.initial_drop_ship_address !== null &&
								cartItem.drop_ship_address.name !==
									cartItem.initial_drop_ship_address.name))
				).length;
	}
}

export default new CartItems();



// WEBPACK FOOTER //
// ./src/state/CartItems.js